/**
 * Role Upload page - Upload resume binders for a role
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roleAPI, Role } from '../api/client';
import TopRibbon from '../components/TopRibbon';
import ProcessingProgress from '../components/ProcessingProgress';
import { useProcessingPoller } from '../hooks/useProcessingPoller';

const RoleUpload: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  const [role, setRole] = useState<Role | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    processing, setProcessing,
    processingStatus,
    retrying, failedTaskId,
    error, setError,
    autoRetryCount, maxAutoRetries,
    startPolling, checkActiveTask, handleRetry,
  } = useProcessingPoller({
    roleId,
    onComplete: () => navigate(`/roles/${roleId}/candidates`),
  });

  const fetchRole = React.useCallback(async () => {
    if (!roleId) return;
    try {
      const response = await roleAPI.getRole(roleId);
      setRole(response.data);
    } catch (error) {
      console.error('Failed to fetch role:', error);
    }
  }, [roleId]);

  useEffect(() => {
    if (roleId) {
      fetchRole();
      checkActiveTask();
    }
  }, [roleId, fetchRole, checkActiveTask]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !roleId) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    const fileSizeMB = selectedFile.size / 1024 / 1024;
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 85) return prev;
        const increment = fileSizeMB > 20 ? 1 : fileSizeMB > 5 ? 3 : 5;
        return Math.min(prev + increment, 85);
      });
    }, fileSizeMB > 20 ? 3000 : 1000);

    try {
      const response = await roleAPI.uploadBinderForRole(roleId, selectedFile);
      setUploadProgress(100);
      clearInterval(progressInterval);

      const backgroundTaskId = response.data.task_id;
      setProcessing(true);
      setUploading(false);
      startPolling(backgroundTaskId);
    } catch (err: any) {
      clearInterval(progressInterval);
      console.error('Upload failed:', err);

      if (err.code === 'ECONNABORTED' && roleId) {
        try {
          const activeTask = await roleAPI.getActiveTask(roleId);
          if (activeTask.data.is_active) {
            setUploading(false);
            setProcessing(true);
            startPolling(activeTask.data.task_id);
            return;
          }
        } catch {
          // No active task found
        }
      }

      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setUploading(false);
    }
  };

  return (
    <>
      <TopRibbon />
      <div style={{ padding: '104px 40px 40px', maxWidth: '800px', margin: '0 auto' }}>
      <button
        onClick={() => navigate('/roles')}
        style={{
          marginBottom: '32px', padding: '10px 20px', background: 'white', color: '#2d5016',
          border: '2px solid #2d5016', borderRadius: '8px', fontSize: '14px', fontWeight: 600,
          cursor: 'pointer', transition: 'all 0.2s', display: 'inline-flex', alignItems: 'center', gap: '8px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#2d5016'; e.currentTarget.style.color = 'white'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#2d5016'; }}
      >
        &larr; Back to Role
      </button>

      {role && role.is_criteria_locked && (
        <div style={{
          padding: '12px 16px', background: '#fef3c7', color: '#92400e',
          borderRadius: '8px', marginBottom: '24px', fontSize: '14px', fontWeight: 600
        }}>
          Criteria are locked. All resumes will be evaluated using the same criteria.
        </div>
      )}

      <div style={{ padding: '40px', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
        <div style={{ border: '2px dashed #d1d5db', borderRadius: '8px', padding: '40px', textAlign: 'center' }}>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            id="file-input"
            disabled={uploading}
          />
          <label
            htmlFor="file-input"
            style={{
              display: 'inline-block', padding: '12px 24px', background: '#2d5016', color: 'white',
              borderRadius: '8px', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1
            }}
          >
            Select PDF Binder
          </label>

          {selectedFile && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontWeight: 600, color: '#1a1a1a' }}>Selected: {selectedFile.name}</p>
              <p style={{ fontSize: '14px', color: '#6b7280' }}>Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
          )}
        </div>

        {uploading && (
          <div style={{ marginTop: '24px' }}>
            <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${uploadProgress}%`, background: '#2d5016', transition: 'width 0.3s' }} />
            </div>
            <p style={{ marginTop: '8px', textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
              Uploading{selectedFile && selectedFile.size > 20 * 1024 * 1024 ? ' large file' : ''}... {uploadProgress}%
              {selectedFile && selectedFile.size > 20 * 1024 * 1024 && uploadProgress < 85 && (
                <span style={{ display: 'block', fontSize: '12px', marginTop: '4px', color: '#9ca3af' }}>
                  Large files may take several minutes to upload and process
                </span>
              )}
            </p>
          </div>
        )}

        {processing && processingStatus && (
          <ProcessingProgress
            processingStatus={processingStatus}
            autoRetryCount={autoRetryCount}
            maxAutoRetries={maxAutoRetries}
          />
        )}

        {error && (
          <div style={{ marginTop: '16px', padding: '12px 16px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', fontSize: '14px' }}>
            <div style={{ marginBottom: failedTaskId ? '12px' : '0' }}>{error}</div>
            {failedTaskId && (
              <button
                onClick={handleRetry}
                disabled={retrying}
                style={{
                  padding: '10px 20px', background: retrying ? '#9ca3af' : '#dc2626', color: 'white',
                  border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600,
                  cursor: retrying ? 'not-allowed' : 'pointer', transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !retrying && (e.currentTarget.style.background = '#b91c1c')}
                onMouseLeave={(e) => !retrying && (e.currentTarget.style.background = '#dc2626')}
              >
                {retrying ? 'Retrying...' : 'Retry Export'}
              </button>
            )}
          </div>
        )}

        {selectedFile && !uploading && !processing && (
          <button
            onClick={handleUpload}
            style={{
              width: '100%', marginTop: '24px', padding: '14px', background: '#2d5016', color: 'white',
              border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Upload & Process Resumes
          </button>
        )}
      </div>

      <div style={{
        marginTop: '24px', padding: '16px', background: '#eff6ff', borderLeft: '3px solid #3b82f6',
        borderRadius: '4px', fontSize: '14px', color: '#1e40af'
      }}>
        <strong>Tip:</strong> The system will automatically extract individual resumes from your binder,
        evaluate each one against the role criteria, and calculate dynamic rankings.
      </div>
    </div>
    </>
  );
};

export default RoleUpload;
